import React, { useState, lazy, Suspense } from 'react';
import TitleHelmet from 'components/TitleHelmet';
import { Link } from 'react-router-dom';
import { useQueryParams } from 'components/hooks/useQueryParams';
import Disclaimer from '../components/Disclaimer';
import 'css/tooltips.css';
import { CalculatorInput, CalculatorWarning } from 'components/calc';
import { useCalculatorStorage } from 'components/hooks/useCalculatorStorage';
import Analytics from '../services/analytics';
import { Slider, ThemeProvider, createTheme } from '@mui/material';
import DoseCalendar from '../components/DoseCalendar';
import { DayOfWeek, DAY_ABBREVIATIONS, FULL_DAY_NAMES } from '../constants/days';

// Lazy load non-critical components
const OtherCalcs = lazy(() => import('components/OtherCalcs'));

// Material UI theme to match your brand colors
const sliderTheme = createTheme({
  palette: {
    primary: {
      main: '#252441',
    },
  },
});

// Custom marks for the slider
const frequencyMarks = [
  { value: 1, label: 'Daily' },
  { value: 2, label: '2 days' },
  { value: 3, label: '3 days' },
  { value: 4, label: '4 days' },
  { value: 5, label: '5 days' },
  { value: 6, label: '6 days' },
];

type SchedulingMode = 'every-n-days' | '2x-per-week';

interface TwiceWeeklyDose {
  day: DayOfWeek;
  dose: number;
  daysUntilNext: number;
}

const SplitDoseCalculator: React.FC = () => {
  // Add responsive styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .responsive-input-row {
        display: flex !important;
        flex-direction: column !important;
      }
      
      @media (min-width: 768px) {
        .responsive-input-row {
          flex-direction: row !important;
        }
      }
      
      .day-picker {
        display: grid !important;
        grid-template-columns: repeat(4, 1fr) !important;
        gap: 8px !important;
      }
      
      @media (min-width: 600px) {
        .day-picker {
          grid-template-columns: repeat(7, 1fr) !important;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const { weeklyDose: queryWeeklyDose, frequency: queryFrequency } = useQueryParams();

  // Use the custom storage hook
  const [weeklyDose, setWeeklyDose] = useCalculatorStorage({
    key: 'splitDose_weeklyDose',
    initialValue: '',
    queryValue: queryWeeklyDose || undefined,
  });

  const [frequency, setFrequency] = useCalculatorStorage({
    key: 'splitDose_frequency',
    initialValue: '5', // Default to 5 days
    queryValue: queryFrequency || undefined,
  });

  // New state for scheduling mode and twice weekly
  const [schedulingMode, setSchedulingMode] = useState<SchedulingMode>('every-n-days');
  const [selectedDays, setSelectedDays] = useState<DayOfWeek[]>([DayOfWeek.MONDAY, DayOfWeek.THURSDAY]);
  const [twiceWeeklyDoses, setTwiceWeeklyDoses] = useState<TwiceWeeklyDose[]>([]);

  const [calculatedDose, setCalculatedDose] = useState<number | null>(null);
  const [showInputs, setShowInputs] = useState<boolean>(true);

  const calculateDaysBetween = (day1: DayOfWeek, day2: DayOfWeek): number => {
    const diff = (day2 - day1 + 7) % 7;
    return diff === 0 ? 7 : diff;
  };

  const calculateTwiceWeeklyDoses = (weeklyDoseValue: number, days: DayOfWeek[]): TwiceWeeklyDose[] => {
    if (days.length !== 2) return [];

    const [firstDay, secondDay] = days.sort((a, b) => a - b);
    const daysFirstToSecond = calculateDaysBetween(firstDay, secondDay);
    const daysSecondToFirst = calculateDaysBetween(secondDay, firstDay);

    const firstDayDose = (daysFirstToSecond / 7) * weeklyDoseValue;
    const secondDayDose = (daysSecondToFirst / 7) * weeklyDoseValue;

    return [
      { day: firstDay, dose: firstDayDose, daysUntilNext: daysFirstToSecond },
      { day: secondDay, dose: secondDayDose, daysUntilNext: daysSecondToFirst },
    ];
  };

  const handleCalculate = (): void => {
    const weeklyDoseValue = parseFloat(weeklyDose);

    if (isNaN(weeklyDoseValue) || weeklyDoseValue <= 0) {
      alert('Please enter a valid weekly dose');
      return;
    }

    if (schedulingMode === 'every-n-days') {
      const frequencyValue = parseInt(frequency);
      if (frequencyValue <= 0) {
        alert('Please select a valid frequency');
        return;
      }

      const newDose = (weeklyDoseValue / 7) * frequencyValue;
      setCalculatedDose(newDose);
      setTwiceWeeklyDoses([]);

      Analytics.track({
        category: 'Calculator',
        action: 'Dose Calculated',
        label: 'SplitDose Calculator',
        properties: {
          weekly_dose: weeklyDoseValue,
          frequency_days: frequencyValue,
          scheduling_mode: 'every_n_days',
          result: Math.round(newDose * 100) / 100,
          calculator_type: 'SplitDose',
        },
      });
    } else {
      if (selectedDays.length !== 2) {
        alert('Please select exactly 2 days for twice weekly dosing');
        return;
      }

      const doses = calculateTwiceWeeklyDoses(weeklyDoseValue, selectedDays);
      setTwiceWeeklyDoses(doses);
      setCalculatedDose(null);

      // For twice weekly, we need to track the total equivalent dose as the result
      const totalWeeklyEquivalent = doses.reduce((sum, dose) => sum + dose.dose, 0);

      Analytics.track({
        category: 'Calculator',
        action: 'Dose Calculated',
        label: 'SplitDose Calculator',
        properties: {
          weekly_dose: weeklyDoseValue,
          scheduling_mode: '2x_per_week',
          selected_days: selectedDays.join(','),
          result: totalWeeklyEquivalent,
          calculator_type: 'SplitDose',
        },
      });
    }

    setShowInputs(false);

    // Scroll to top when calculation is submitted for better UX
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShowInputs = (): void => {
    setShowInputs(true);
    setCalculatedDose(null);
    setTwiceWeeklyDoses([]);
  };

  const handleFrequencyChange = (event: Event, newValue: number | number[]) => {
    setFrequency(String(newValue as number));
  };

  const handleSchedulingModeChange = (mode: SchedulingMode) => {
    setSchedulingMode(mode);
    if (mode === '2x-per-week' && selectedDays.length !== 2) {
      setSelectedDays([DayOfWeek.MONDAY, DayOfWeek.THURSDAY]);
    }
  };

  const handleDayToggle = (day: DayOfWeek) => {
    setSelectedDays(prev => {
      if (prev.includes(day)) {
        // Remove the day
        return prev.filter(d => d !== day);
      } else if (prev.length < 2) {
        // Add the day
        return [...prev, day];
      } else {
        // Replace the first selected day with the new one
        return [prev[1], day];
      }
    });
  };

  const frequencyDays = parseInt(frequency);
  const frequencyText = frequencyDays === 1 ? 'daily' : `every ${frequencyDays} days`;

  return (
    <>
      <div className="App">
        <TitleHelmet
          title="Split Dose Calculator - Convert Weekly Doses to Custom Schedules"
          description="Calculate equivalent doses when switching from weekly injections to more frequent schedules. Perfect for better tolerance or more consistent medication levels."
        />

        <header className="App-header">
          <h1>Split Dose Calculator</h1>
        </header>

        {showInputs ? (
          <div
            className="calculator-container"
            style={{ maxWidth: '100%', overflow: 'hidden' }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') handleCalculate();
            }}
          >
            <div className="input-container" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
              <div>
                Want to split your weekly dose into smaller, more frequent injections? This calculator helps you plan
                your doses when switching to a split-dose schedule.
              </div>

              {/* Responsive layout: side-by-side on desktop, stacked on mobile */}
              <div className="responsive-input-row">
                <CalculatorInput
                  id="weeklyDose"
                  value={weeklyDose}
                  onChange={setWeeklyDose}
                  unit="mg"
                  placeholder="e.g., 10"
                  label="Desired Weekly Dose (mg)"
                />

                <div className="input-box">
                  <label>Dosing Schedule Type</label>
                  <div
                    className="scheduling-mode-tabs"
                    style={{
                      display: 'flex',
                      border: '2px solid #252441',
                      borderRadius: '4px',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleSchedulingModeChange('every-n-days')}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: 'none',
                        backgroundColor: schedulingMode === 'every-n-days' ? '#252441' : 'white',
                        color: schedulingMode === 'every-n-days' ? 'white' : '#252441',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        borderRight: '1px solid #252441',
                      }}
                    >
                      Every Few Days
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSchedulingModeChange('2x-per-week')}
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: 'none',
                        backgroundColor: schedulingMode === '2x-per-week' ? '#252441' : 'white',
                        color: schedulingMode === '2x-per-week' ? 'white' : '#252441',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                      }}
                    >
                      Twice Weekly
                    </button>
                  </div>
                </div>
              </div>

              {/* Full-width scheduling controls */}
              <div className="scheduling-controls" style={{ width: '100%' }}>
                {schedulingMode === 'every-n-days' ? (
                  <div className="frequency-slider-container">
                    <div className="frequency-display" style={{ marginBottom: '15px', textAlign: 'center' }}>
                      Take doses <strong>{frequencyText}</strong>
                    </div>
                    <ThemeProvider theme={sliderTheme}>
                      <Slider
                        value={Number(frequency)}
                        onChange={handleFrequencyChange}
                        aria-labelledby="frequency-slider-label"
                        valueLabelDisplay="off"
                        step={1}
                        marks={frequencyMarks}
                        min={1}
                        max={6}
                        sx={{
                          width: '100%',
                          '& .MuiSlider-markLabel': {
                            fontSize: '0.8rem',
                            color: '#666',
                            marginTop: '8px',
                          },
                          '& .MuiSlider-mark': {
                            backgroundColor: '#252441',
                            height: 8,
                            width: 2,
                          },
                          '& .MuiSlider-track': {
                            backgroundColor: '#252441',
                          },
                          '& .MuiSlider-thumb': {
                            backgroundColor: '#252441',
                            '&:hover': {
                              backgroundColor: '#0056b3',
                            },
                          },
                        }}
                      />
                    </ThemeProvider>
                  </div>
                ) : (
                  <div className="day-picker-container">
                    <div style={{ marginBottom: '15px', textAlign: 'center' }}>Select your two injection days:</div>
                    <div
                      className="day-picker"
                      style={{
                        marginBottom: '15px',
                        maxWidth: '100%',
                      }}
                    >
                      {Object.entries(DAY_ABBREVIATIONS).map(([dayValue, dayName]) => {
                        const day = parseInt(dayValue) as DayOfWeek;
                        const isSelected = selectedDays.includes(day);
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => handleDayToggle(day)}
                            style={{
                              aspectRatio: '1',
                              border: '2px solid #252441',
                              backgroundColor: isSelected ? '#252441' : 'white',
                              color: isSelected ? 'white' : '#252441',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              fontSize: '0.9em',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '50px',
                            }}
                          >
                            {dayName}
                          </button>
                        );
                      })}
                    </div>
                    {selectedDays.length === 2 && (
                      <div style={{ fontSize: '0.9em', color: '#666', textAlign: 'center' }}>
                        Selected: {selectedDays.map(day => FULL_DAY_NAMES[day]).join(' and ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <button onClick={handleCalculate} className="calculate-button">
              Calculate Split Dose
            </button>

            <div className="smaller-text" style={{ marginTop: '15px' }}>
              <strong>Why split doses?</strong> Some people find smaller, more frequent doses easier to tolerate with
              fewer side effects, while maintaining more consistent medication levels and hunger. Before adjusting your
              medication schedule, please consult your doctor.
            </div>
          </div>
        ) : (
          <span>
            &#9664;{' '}
            <Link className="smaller-text" onClick={handleShowInputs} to="">
              Reset calculator and start again
            </Link>
          </span>
        )}

        {(calculatedDose !== null || twiceWeeklyDoses.length > 0) && (
          <div className="result centered">
            <div>
              {parseFloat(weeklyDose) <= 0.1 && (
                <CalculatorWarning
                  type="urgent"
                  message="Weekly doses below 0.1mg are extremely low. Please verify your current dose carefully."
                />
              )}

              {parseFloat(weeklyDose) > 20 && (
                <CalculatorWarning
                  type="urgent"
                  message="Weekly doses above 20mg are unusually high. Please verify your prescription and calculations carefully."
                />
              )}

              {((schedulingMode === 'every-n-days' && frequencyDays <= 2) ||
                (schedulingMode === '2x-per-week' &&
                  selectedDays.length === 2 &&
                  (calculateDaysBetween(selectedDays[0], selectedDays[1]) === 1 ||
                    calculateDaysBetween(selectedDays[1], selectedDays[0]) === 1))) && (
                <CalculatorWarning message="Very frequent dosing (daily or every 2 days) may require medical supervision. Consult your healthcare provider before switching to this schedule." />
              )}

              <div className="result centered">
                {schedulingMode === 'every-n-days' ? (
                  <div>
                    To get the equivalent of {weeklyDose}mg weekly,
                    <br />
                    <div className="resultsText">
                      you can take {calculatedDose!.toFixed(2)}mg <br />
                      {frequencyText}
                    </div>
                  </div>
                ) : (
                  <div>
                    To split {weeklyDose}mg weekly into 2 doses on fixed days,
                    <div className="resultsText">
                      You can take {twiceWeeklyDoses[0]?.dose.toFixed(2)}mg each{' '}
                      {FULL_DAY_NAMES[twiceWeeklyDoses[0]?.day]},
                      <br />
                      and {twiceWeeklyDoses[1]?.dose.toFixed(2)}mg each {FULL_DAY_NAMES[twiceWeeklyDoses[1]?.day]}.
                    </div>
                  </div>
                )}
              </div>

              {schedulingMode === 'every-n-days' ? (
                <DoseCalendar frequency={frequencyDays} dosage={calculatedDose!} showStartingDaySelector={true} />
              ) : (
                <DoseCalendar
                  customDoseDays={twiceWeeklyDoses.map(dose => ({ day: dose.day, dose: dose.dose }))}
                  startingDay={Math.min(...selectedDays)}
                  showOneWeek={true}
                />
              )}

              <div style={{ marginTop: '25px' }}>
                <div
                  style={{
                    backgroundColor: '#f8f9fa',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    textAlign: 'left',
                  }}
                >
                  {schedulingMode === 'every-n-days' ? (
                    <>
                      <h3>Next: Calculate Your Injection Volume:</h3>
                      <div
                        style={{
                          marginBottom: '0',
                          fontSize: '1.05em',
                        }}
                      >
                        <strong>Take {frequencyText}:</strong> {calculatedDose!.toFixed(2)}mg — Calculate this dosage in
                        the{' '}
                        <Link to={`/dosage-calculator?d=${calculatedDose!.toFixed(2)}`}>Tirzepatide Calculator</Link> or
                        the{' '}
                        <Link to={`/semaglutide-calculator?d=${calculatedDose!.toFixed(2)}`}>
                          Semaglutide Calculator
                        </Link>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3>Next: Calculate the Injection Volume for Each Dose</h3>
                      {twiceWeeklyDoses.map((doseInfo, index) => (
                        <div
                          key={doseInfo.day}
                          style={{
                            marginBottom: index < twiceWeeklyDoses.length - 1 ? '15px' : '0',
                            fontSize: '1.05em',
                          }}
                        >
                          <strong>{FULL_DAY_NAMES[doseInfo.day]}:</strong> {doseInfo.dose.toFixed(2)}mg — Calculate this
                          dosage in the{' '}
                          <Link to={`/dosage-calculator?d=${doseInfo.dose.toFixed(2)}`}>Tirzepatide Calculator</Link> or
                          the{' '}
                          <Link to={`/semaglutide-calculator?d=${doseInfo.dose.toFixed(2)}`}>
                            Semaglutide Calculator
                          </Link>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <Disclaimer />

        <Suspense fallback={<div>Loading more calculators...</div>}>
          <OtherCalcs />
        </Suspense>
      </div>
    </>
  );
};

export default SplitDoseCalculator;
